import { Box } from "@mui/material";
import i18n from "i18next";
import { forwardRef, useImperativeHandle, useState } from "react";
import type { CustomFunctionFileInput } from "src/api/generated/erp/customFunctions/model/customFunctionFileInput.ts";
import { AavoFileInput } from "src/components/common/inputFields/fileInput/AavoFileInput.tsx";
import { toRjsfSchema } from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import {
	isAcceptedCustomFunctionInputFile,
	mergeCustomFunctionFileInputErrors,
	type SelectedCustomFunctionInputFiles,
	type CustomFunctionInputFileValidationError,
	validateCustomFunctionInputFiles,
	withCustomFunctionFileInputError,
} from "src/components/common/customFunctions/customFunctionFileInputUtils.ts";

export interface CustomFunctionFileInputsRef {
	getValidatedFiles: () => SelectedCustomFunctionInputFiles | undefined;
}

interface CustomFunctionFileInputsProps {
	definitions: CustomFunctionFileInput[];
	inputSchema: unknown;
	onEdited: () => void;
}

export const CustomFunctionFileInputs = forwardRef<CustomFunctionFileInputsRef, CustomFunctionFileInputsProps>(
	function CustomFunctionFileInputs({ definitions, inputSchema, onEdited }, ref) {
		const [selected, setSelected] = useState<SelectedCustomFunctionInputFiles>({});
		const [selectionErrors, setSelectionErrors] = useState<Record<string, string>>({});
		const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
		const errors = mergeCustomFunctionFileInputErrors(validationErrors, selectionErrors);
		const schema = toRjsfSchema(inputSchema);
		const requiredFields = new Set(Array.isArray(schema.required) ? schema.required : []);

		useImperativeHandle(ref, () => ({ getValidatedFiles }));

		if (definitions.length === 0) return null;
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, padding: 2 }}>
				{definitions.map((definition) => {
					const files = selected[definition.inputField] ?? [];
					const propertySchema = toRjsfSchema(schema.properties?.[definition.inputField]);
					const commonProps = {
						label: typeof propertySchema.title === "string" ? propertySchema.title : definition.inputField,
						required: requiredFields.has(definition.inputField),
						accept: definition.acceptedMimeTypes.join(",") || "*/*",
						error: errors[definition.inputField],
					};
					return definition.multiple ?
						<AavoFileInput
							key={definition.inputField}
							{...commonProps}
							multiple={true}
							value={files}
							onChange={(value) => changeFiles(definition, value)}
						/>
					: 	<AavoFileInput
							key={definition.inputField}
							{...commonProps}
							multiple={false}
							value={files[0] ?? null}
							onChange={(value) => changeFiles(definition, value == null ? [] : [value])}
						/>;
				})}
			</Box>
		);

		function changeFiles(definition: CustomFunctionFileInput, files: File[]) {
			const rejected = files.some((file) =>
				!isAcceptedCustomFunctionInputFile(file, definition.acceptedMimeTypes),
			);
			setSelectionErrors((current) =>
				withCustomFunctionFileInputError(
					current,
					definition.inputField,
					rejected ? i18n.t("custom_function_file_type_not_accepted") : undefined,
				),
			);
			setValidationErrors({});
			setSelected((current) => ({
				...current,
				[definition.inputField]: files.filter((file) =>
					isAcceptedCustomFunctionInputFile(file, definition.acceptedMimeTypes),
				),
			}));
			onEdited();
		}

		function getValidatedFiles(): SelectedCustomFunctionInputFiles | undefined {
			const validationErrors = validateCustomFunctionInputFiles(definitions, requiredFields, selected);
			const nextErrors = Object.fromEntries(
				Object.entries(validationErrors).map(([field, error]) => [field, validationErrorMessage(error)]),
			);
			setValidationErrors(nextErrors);
			const allErrors = mergeCustomFunctionFileInputErrors(nextErrors, selectionErrors);
			return Object.keys(allErrors).length === 0 ? selected : undefined;
		}
	},
);

function validationErrorMessage(error: CustomFunctionInputFileValidationError): string {
	switch (error) {
		case "required":
			return i18n.t("required_field");
		case "file_too_large":
			return i18n.t("custom_function_file_too_large");
		case "too_many_files":
			return i18n.t("custom_function_too_many_files");
		case "total_too_large":
			return i18n.t("custom_function_files_too_large");
	}
}
